import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import { Box } from '@mui/material';
import { WeaponPresentation } from '../../../types/ui/weaponPresentation';


interface Props {
  data: WeaponPresentation;
}

export default function SideViewChart({ data }: Props) {
  const { sideView: points, metrics, eventsInfo } = data;
  if (!points || points.length === 0) return null;
  const startTime = points[0].x;
  const start = points[0];
  const end = points[points.length - 1];

  const formatPoint = (p: { x: number; y: number }) => ({
    x: +(p.x - startTime).toFixed(2),
    y: +p.y.toFixed(2),
  });

  const formattedPoints = points.map(formatPoint);
  const formattedStart = formatPoint(start);
  const formattedEnd = formatPoint(end);
  if (eventsInfo?.presentationHeadX != null && eventsInfo?.presentationHeadY != null) {
    const headX = +(eventsInfo.presentationHeadX - startTime).toFixed(2);
    const headY = +eventsInfo.presentationHeadY.toFixed(2);

    console.log('📌 Head Position:', { headX, headY });
  }

  const headPoint =
    eventsInfo?.presentationHeadX !== undefined && eventsInfo?.presentationHeadY !== undefined
      ? {
        x: +(eventsInfo.presentationHeadX - startTime).toFixed(2),
        y: +eventsInfo.presentationHeadY.toFixed(2)
      }
      : null;

  // Calculate max for ticks
  const maxX = Math.max(...formattedPoints.map(p => p.x));
  const maxY = Math.max(...formattedPoints.map(p => p.y));
  // Generate ticks at intervals of 5
  const getTicks = (max: number, min = 0, step = 10) => {
    const ticks = [];
    for (let i = min; i <= Math.ceil(max / step) * step + step; i += step) {
      ticks.push(i);
    }
    return ticks;
  };



  // Calculate dynamic domain max (rounded up to next 5, plus 5)
  const domainMaxX = Math.min(30, Math.ceil(maxX / 5) * 5 + 5);
  const domainMaxY = Math.min(30, Math.ceil(maxY / 5) * 5 + 5);
  const xTicks = getTicks(domainMaxX, -40, 10);
  const yTicks = getTicks(domainMaxY, 0, 10);
  return (
    <Box
      width={{ xs: '100%', md: 470 }}
      height={420}
      sx={{
        outline: '0.01rem solid #ccc',
        borderRadius: 0,
        backgroundColor: '#142831',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          margin={{ top: 40, right: 40, bottom: 50, left: 10 }}
          data={formattedPoints}
          style={{ backgroundColor: '#142831', borderRadius: 8 }}
        >
          <CartesianGrid stroke="#2c3e50" strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="x"
            domain={[-30, domainMaxX]}
            allowDataOverflow={false}
            stroke="#AAAAAA"
            tick={{ fill: '#CCCCCC', fontSize: 12 }}
            tickFormatter={v => `${v.toFixed(0)} cm`}
            ticks={xTicks}
            label={{

              position: 'insideBottomRight',
              offset: -10,
              fill: '#DDDDDD',
              fontSize: 13,
            }}
          />

          <YAxis
            type="number"
            dataKey="y"
            domain={[-5, domainMaxY]}
            allowDataOverflow={false}
            stroke="#AAAAAA"
            tick={{ fill: '#CCCCCC', fontSize: 12 }}
            tickFormatter={v => v < 0 ? '' : `${v.toFixed(0)} cm`}
            ticks={yTicks}
            label={{

              angle: -90,
              position: 'insideLeft',
              offset: 10,
              fill: '#DDDDDD',
              fontSize: 13,
            }}
          />

          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{
              backgroundColor: '#1f2f38',
              borderColor: '#888',
              borderRadius: 8,
              fontSize: 13,
              color: '#fff',
            }}
            labelFormatter={(v) => `${v} ms`}
            formatter={(value: number, name: string) =>
              name === 'y' ? [`${value.toFixed(2)} m`, 'Height'] : value
            }
          />
          <Line type="monotone" dataKey="y" stroke="#FFFFFF" strokeWidth={2} dot={false} />
          <Line
            type="linear"
            data={[formattedStart, formattedEnd]}
            dataKey="y"
            stroke="#00B8FF"
            strokeWidth={2}
            dot={false}
          />
          {eventsInfo?.presentationHeadX != null && eventsInfo?.presentationHeadY != null && (
            <ReferenceDot
              x={+(eventsInfo.presentationHeadX - startTime).toFixed(2)}
              y={+eventsInfo.presentationHeadY.toFixed(2)}
              r={5}
              fill="#FFFFFF"
              stroke="#FFFFFF"
              isFront
            />
          )}

          <ReferenceDot
            x={formattedStart.x}
            y={formattedStart.y}
            r={6}
            fill="#3ec46d"
            stroke="#3ec46d"
            isFront
          />
          <ReferenceDot
            x={formattedEnd.x}
            y={formattedEnd.y}
            r={6}
            fill="#FFA500" // Orange
            stroke="#FFA500"
            
            isFront
          />
          {headPoint && (
            <ReferenceDot
              x={headPoint.x}
              y={headPoint.y}
              r={5}
              fill="#FFFFFF"
              stroke="#FFFFFF"
              isFront
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
